import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {MenuTestPage} from '../pages/MenuTestPage'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import BuilderPageWrapper from '../pages/layout-builder/BuilderPageWrapper'
import {Logout, AuthPage, useAuth} from '../modules/auth'

const PrivateRoutes = () => {
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
  const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))
  const PatientPage = lazy(() => import('../modules/apps/patient/PatientPage'))
  const DoctorSchedulePage = lazy(() => import('../modules/apps/doctor-schedule/DoctorSchedulePage'))
  const PatientSchedulePage = lazy(() => import('../modules/apps/patient-schedule/PatientSchedulePage'))
  const SettingPage = lazy(() => import('../modules/apps/setting/SettingPage'))
  const AppointmentPage = lazy(() => import('../modules/apps/appointment/AppointmentPage'))
  const TreatmentPage = lazy(() => import('../modules/apps/treatment/Page'))
  const CrmPage = lazy(() => import('../modules/apps/crm/Page'))
  const ProductSalesPage = lazy(() => import('../modules/apps/product-sales/Page'))
  const LabPage = lazy(() => import('../modules/apps/lab/PatientPage'))
  const FormPage = lazy(() => import('../modules/apps/form/SettingPage'))
  const AccountingPage = lazy(() => import('../modules/apps/accounting/SettingPage'))
  const ReportPage = lazy(() => import('../modules/apps/report/SettingPage'))
  const HandbookPage = lazy(() => import('../modules/apps/handbook/SettingPage'))
  const FollowUpPage = lazy(() => import('../modules/apps/follow-up/FollowUpPage'))
  const {currentUser} = useAuth()

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path='auth/*' element={<Navigate to={currentUser?.roomId?'/schedule':'/dashboard'} />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />
        <Route path='builder' element={<BuilderPageWrapper />} />
        <Route path='menu-test' element={<MenuTestPage />} />
        {/* Lazy Modules */}
        <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        />
        <Route
          path='patient/*'
          element={
            <SuspensedView>
              <PatientPage />
            </SuspensedView>
          }
        />
        <Route
          path='doctor-schedule/*'
          element={
            <SuspensedView>
              <DoctorSchedulePage />
            </SuspensedView>
          }
        />
        <Route
          path='schedule/*'
          element={
            <SuspensedView>
              <PatientSchedulePage />
            </SuspensedView>
          }
        />
        <Route
          path='setting/*'
          element={
            <SuspensedView>
              <SettingPage />
            </SuspensedView>
          }
        />
        <Route
          path='appointment/*'
          element={
            <SuspensedView>
              <AppointmentPage />
            </SuspensedView>
          }
        />
        <Route
          path='treatment/*'
          element={
            <SuspensedView>
              <TreatmentPage />
            </SuspensedView>
          }
        />
        <Route
          path='crm/*'
          element={
            <SuspensedView>
              <CrmPage />
            </SuspensedView>
          }
        />
         <Route
          path='follow-up/*'
          element={
            <SuspensedView>
              <FollowUpPage />
            </SuspensedView>
          }
        />
        <Route
          path='product-sales/*'
          element={
            <SuspensedView>
              <ProductSalesPage />
            </SuspensedView>
          }
        />

        <Route
          path='lab/*'
          element={
            <SuspensedView>
              <LabPage />
            </SuspensedView>
          }
        />

        <Route
          path='form/*'
          element={
            <SuspensedView>
              <FormPage />
            </SuspensedView>
          }
        />

        <Route
          path='accounting/*'
          element={
            <SuspensedView>
              <AccountingPage />
            </SuspensedView>
          }
        />

        <Route
          path='report/*'
          element={
            <SuspensedView>
              <ReportPage />
            </SuspensedView>
          }
        />
        <Route
          path='handbook/*'
          element={
            <SuspensedView>
              <HandbookPage />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
